
import { Component, Emit, Prop, Ref, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

// hugerte imports
import hugerte, { Editor, RawEditorOptions } from "hugerte"
import "hugerte/icons/default"
import "hugerte/themes/silver"
import "hugerte/skins/ui/oxide/skin.css"
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
import contentCss from "!!raw-loader!hugerte/skins/content/default/content.min.css"
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
import contentUiCss from "!!raw-loader!hugerte/skins/ui/oxide/content.min.css"

import "hugerte/plugins/advlist"
import "hugerte/plugins/autolink"
import "hugerte/plugins/fullscreen"
import "hugerte/plugins/autoresize"
import "hugerte/plugins/image"
import "hugerte/plugins/lists"
import "hugerte/plugins/link"
import "hugerte/plugins/preview"
import "hugerte/plugins/searchreplace"
import "hugerte/plugins/table"
import "hugerte/plugins/pagebreak"
import "hugerte/plugins/anchor"
import "hugerte/models/dom"
import "hugerte/plugins/visualblocks"

import HyperlinkModal from "@/components/Controls/HyperlinkModal.vue"
import SearchDropDown from "@/components/Controls/SearchDropDown.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { StylingConstants } from "@/constants/styling"

const setup = namespace("setup")

type Hyperlink = {
	text: string,
	url: string,
	replaceExistingText: boolean
}

const focusIn = function (e: FocusEvent) {
	const target = e.target as Element
	const closest = target.closest(".tox-hugerte-aux, .tox-dialog, .moxman-window, .tam-assetmanager-root")
	if (closest !== null && closest !== undefined) {
		e.stopImmediatePropagation()
	}
}

@Component({
	components: {
		"hyperlink-modal": HyperlinkModal,
		"search-dropdown": SearchDropDown
	}
})
export default class RichTextEditorHugeRTE extends Vue {
	// ---- Refs ----

	@Ref()
	editorContainer!: HTMLTextAreaElement

	@Ref()
	editorParent!: HTMLElement

	@Ref()
	fileInput!: HTMLInputElement

	// ---- Props ----

	/**
	 * Value for v-model support
	 */
	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ default: () => null })
	public placeholder!: string | null

	@Prop({ default: null })
	public minHeight!: string | null

	@Prop({ type: Boolean, default: false })
	public applyMaxHeight!: boolean

	@Prop({ type: Array, default: () => null })
	public placeholders!: { id: string, label: string }[] | null

	@Prop({ type: Boolean, default: true })
	public allowImages!: boolean

	// ---- VueX ----

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getDarkMode") darkMode!: boolean

	// ---- Data ----

	private editor: Editor | null = null
	private focusWhenReady: boolean = false

	private plugins: string[] =
	[
		"advlist", "autolink", "fullscreen", "image", "lists", "link", "searchreplace", "anchor", "visualblocks", "autoresize"
	]

	private ignoredEditorCommands = ["mceFocus", "SelectAll", "mceInsertContent", "mceInsertLink"]

	// ---- Computed Properties ----

	get richTextImagesEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.RichTextImages) && this.allowImages
	}

	get workflowAdjustmentForPictureAttachmentsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.WorkflowAdjustmentForPictureAttachments)
	}

	private get contentStyle (): string {
		// This workaround is needed because the HugeRTE editor text input loads in an iframe which bypasses our global css.
		// This only applies to the actual text input, not the editor's toolbar, dialogs, etc.
		const themeVariables = this.darkMode ? StylingConstants.dark : StylingConstants.light
		return (`
			body {
				color: ${themeVariables["$body-color"]};
				background-color: ${themeVariables["$input-bg"]};
			}

			body::before {
				color: ${themeVariables["$vs-component-placeholder-color"]} !important;
			}

			a {
				color: ${themeVariables["$link-color"]};
			}

			.mce-visualblocks * {
				background-image: none !important;
				border: none !important;
			}
		`)
	}

	// ---- Lifecycle Hooks ----

	private async mounted (): Promise<void> {
		await this.initialiseHugeRTE()
	}

	private beforeDestroy (): void {
		document.removeEventListener("focusin", focusIn)

		if (!this.editor) {
			return
		}

		this.editor.destroy()
	}

	// ---- Watchers ----

	@Watch("value")
	private valueChanged (newValue: string) {
		if (this.editor && newValue !== this.editor.getContent()) {
			this.editor.setContent(newValue)
		}
	}

	@Watch("readonly")
	private onReadonlyChanged (readonly: boolean) : void{
		if (!this.editor) {
			return
		}

		this.editor!.mode.set(readonly ? "readonly" : "design")

		if (this.focusWhenReady && !readonly) {
			this.focusWhenReady = false
			this.editor!.focus()
		}
	}

	@Watch("darkMode")
	public onDarkModeChanged (): void {
		// Reinitialize the editor to apply the new theme
		if (this.editor) this.editor.destroy()
		this.initialiseHugeRTE()
	}

	@Emit("input")
	private emitInput (value: string): string {
		return value
	}

	// ----Utils ----

	private async uploadImage (blobInfo: any, progress: any): Promise<string> {
		return "data:image/png;base64," + blobInfo.base64()
	}

	/**
	 * Set editing handlers and listeners
	 * @param editor edit to configure
	 */
	private onEditorSetup (editor: Editor) : void {
		editor.on("input", () => this.emitInputIfChanged(editor.getContent()))

		editor.on("change", async () => {
			// wait to ensure the 'input' event hasn't already handled the change, as there's some overlap between the two events.
			await this.$nextTick()
			await this.$nextTick()

			const content = editor.getContent()
			if (this.value !== content) {
				this.emitInputIfChanged(content)
			}
		})

		// when a user uses a button emit the content up.
		editor.on("ExecCommand", (commandArgs: { command: string }) => {
			if (this.ignoredEditorCommands.includes(commandArgs.command)) return

			this.emitInputIfChanged(editor.getContent())
		})

		editor.on("focus", () => this.$emit("focus"))

		editor.on("blur", () => {
			this.$emit("blur")
		})

		// this resolves an issue where you cannot interact with the HugeRTE dialogues within BModal.
		document.addEventListener("focusin", focusIn)

		// this handles the default tab setting for the upload image dialog
		editor.on("OpenWindow", (e) => {
			if (this.workflowAdjustmentForPictureAttachmentsEnabled) {
				const dialog = document.querySelector(".tox-dialog__body")
				if (dialog) {
					const dialogTitle = document.querySelector(".tox-dialog__title")
					// Check if opened dialog is image uploader
					if (dialogTitle && dialogTitle.textContent === "Insert/Edit Image") {
						const tabList = dialog.querySelector(".tox-dialog__body-nav")
						if (tabList) {
							const tabs = tabList.querySelectorAll(".tox-tab")
							const generalTab = tabs[0] as HTMLElement
							const uploadTab = tabs[1] as HTMLElement

							// Move "Upload" tab above "General" tab
							if (uploadTab.parentElement) {
								uploadTab.parentElement.insertBefore(uploadTab, generalTab)
							}

							// Activate the "Upload" tab
							(uploadTab as HTMLElement).click()
							uploadTab.focus()
						}
					}
				}
			}
		})
	}

	/**
	 * Set up hugeRTE
	 */
	private async initialiseHugeRTE (): Promise<void> {
		const hugeRTEConfig: RawEditorOptions =
		{
			target: this.editorContainer,
			min_height: 100,
			resize: false,
			plugins: this.plugins,
			toolbar: "undo redo | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist | h1 h2 h3 | link" + (this.allowImages ? " image" : ""),
			promotion: false,
			visualblocks_default_state: true,
			noneditable_class: "ql-placeholder-content",
			menubar: "",
			setup: this.onEditorSetup,
			branding: false,
			images_upload_handler: this.uploadImage,
			elementpath: false,
			statusbar: false,
			placeholder: this.placeholder ? this.placeholder : undefined,
			link_default_target: "_blank",
			link_target_list: false,
			link_title: false,
			link_assume_external_targets: "http",
			content_style: [contentCss, contentUiCss, this.contentStyle].join("\n"),
			readonly: this.readonly
		}

		const editor = (await hugerte.init(hugeRTEConfig))[0]
		Vue.set(this, "editor", editor)
	}

	private emitInputIfChanged (value: string): void {
		// Ensure the value has changed and is not going from null to an empty string.
		if (value !== this.value && !(value.length === 0 && !this.value)) {
			this.emitInput(value)
		}
	}
}
